import { DateTime, Settings } from "luxon";

import { HourlyMetricsCardEntryDTO } from "../../../DTOs/Dashboards/HourlyMetricsCardEntryDTO";
import { PlantTypeDTO } from "../../../DTOs/PlantTypeDTO";

export interface HourlyMetricsChartPoint extends Omit<HourlyMetricsCardEntryDTO, "utilization" | "availability"> {
  displayHour: string;
  utilization: number | null;
  availability: number | null;
}

export interface HourlyMetricsChartData {
  points: HourlyMetricsChartPoint[];
  liveEdgeIndex: number;
}

const MILLIS_PER_HOUR = 3_600_000;

/**
 * Index of the current hour, -1 when the reference time is before the shift starts.
 * Clamped to the last slot so a shift that has run past its window stays fully drawn.
 */
export const resolveElapsedHourIndex = (
  shiftStartMillis: number,
  referenceMillis: number,
  hourCount: number
): number => {
  if (hourCount <= 0) return -1;

  const elapsed = Math.floor((referenceMillis - shiftStartMillis) / MILLIS_PER_HOUR);
  if (elapsed < 0) return -1;

  return Math.min(elapsed, hourCount - 1);
};

/**
 * The backend returns a row for every hour of the shift, and hours that have not happened yet
 * come back as zeros rather than nulls. Blanking the actuals past the hour in progress stops the
 * Utilization and Availability lines at the live edge instead of dragging them down to 0%.
 * Only the shift in progress is trimmed; a completed shift is all actuals.
 */
export const buildHourlyMetricsChartData = (
  metrics: HourlyMetricsCardEntryDTO[] | null,
  referenceMillis: number,
  isCurrentShift: boolean
): HourlyMetricsChartData => {
  if (!metrics?.length) return { points: [], liveEdgeIndex: -1 };

  const shiftStart = metrics[0].shiftStartUtc.setZone(Settings.defaultZone).startOf("hour");
  const liveEdgeIndex = isCurrentShift
    ? resolveElapsedHourIndex(shiftStart.toMillis(), referenceMillis, metrics.length)
    : metrics.length - 1;

  const points = metrics.map((entry, i) => ({
    ...entry,
    displayHour: shiftStart.plus({ hours: i }).toFormat("HH:00"),
    utilization: i > liveEdgeIndex ? null : entry.utilization,
    availability: i > liveEdgeIndex ? null : entry.availability,
  }));

  return { points, liveEdgeIndex };
};

/**
 * The type running the most production excavators, so the card opens on the biggest fleet. */
export const pickDefaultPlantType = (plantTypes: PlantTypeDTO[]): PlantTypeDTO | null =>
  plantTypes.reduce<PlantTypeDTO | null>(
    (best, candidate) =>
      best === null || (candidate.activePlantCount ?? 0) > (best.activePlantCount ?? 0) ? candidate : best,
    null
  );

export const hourlyMetricsReferenceMillis = (dataUpdatedAt: number) =>
  dataUpdatedAt > 0 ? dataUpdatedAt : DateTime.now().toMillis();
